import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Print as PrintIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { consumableService } from '../services/consumableService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../hooks/useAuth';
import ReportExportMenu from '../components/ReportExportMenu';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import '../styles/print.css';

const employeeLabel = (e) =>
  e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || `#${e.id}` : '';

const formatTs = (value) => {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return format(d, 'MM/dd/yyyy h:mm a');
  } catch {
    return String(value);
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const raw = String(value).slice(0, 10);
    return format(parseISO(raw), 'MM/dd/yyyy');
  } catch {
    return String(value);
  }
};

const lineStatus = (line) => {
  if (line.counted_qty == null || line.counted_qty === '') return 'Not counted';
  if (Number(line.counted_qty) === Number(line.system_qty)) return 'Verified';
  return 'Discrepancy';
};

const statusChip = (status) => {
  if (status === 'Verified') return <Chip size="small" label="Verified" color="success" variant="outlined" />;
  if (status === 'Discrepancy') return <Chip size="small" label="Discrepancy" color="warning" />;
  if (status === 'posted') return <Chip size="small" label="Posted" color="success" variant="outlined" />;
  if (status === 'open') return <Chip size="small" label="Open" color="info" variant="outlined" />;
  if (status === 'cancelled') return <Chip size="small" label="Cancelled" variant="outlined" />;
  return <Chip size="small" label={status} variant="outlined" />;
};

const Consumables = ({ catalogType = 'consumable' }) => {
  const { isEmployeeOrAdmin, user } = useAuth();
  const canManage = isEmployeeOrAdmin();
  const isRaw = catalogType === 'raw_material';
  const pageTitle = isRaw ? 'Raw materials' : 'Consumables';
  const pageSubtitle = isRaw
    ? 'Track sheet metal and other raw stock by category, with on-hand counts and weekly audits.'
    : 'Track shop consumables by category and run weekly Monday counts.';
  const noun = isRaw ? 'raw materials' : 'consumables';
  const fileInputRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState('');
  const [importSaving, setImportSaving] = useState(false);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedCats, setExpandedCats] = useState({});
  const [employees, setEmployees] = useState([]);

  const [audits, setAudits] = useState([]);
  const [activeAudit, setActiveAudit] = useState(null);
  const [auditSaving, setAuditSaving] = useState(false);
  const [lowStock, setLowStock] = useState([]);

  // Dialogs
  const [catDialog, setCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ id: null, name: '' });
  const [itemDialog, setItemDialog] = useState(false);
  const [itemForm, setItemForm] = useState({
    id: null,
    category_id: '',
    name: '',
    part_number: '',
    thickness: '',
    sheet_size: '',
    quantity_on_hand: 0,
    minimum_quantity: 0,
    unit: '',
    notes: ''
  });
  const [qtyDialog, setQtyDialog] = useState(false);
  const [qtyForm, setQtyForm] = useState({ id: null, name: '', quantity: 0, notes: '' });
  const [newAuditOpen, setNewAuditOpen] = useState(false);
  const [newAuditDate, setNewAuditDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [newAuditPerformer, setNewAuditPerformer] = useState(null);

  const loadCatalog = useCallback(async () => {
    const [catRes, itemRes] = await Promise.all([
      consumableService.getCategories({ activeOnly: 'true' }, catalogType),
      consumableService.getItems(
        { activeOnly: 'true', search: search || undefined, limit: 1000 },
        catalogType
      )
    ]);
    const cats = catRes.data || [];
    setCategories(cats);
    setItems(itemRes.data || []);
    setExpandedCats((prev) => {
      const next = { ...prev };
      cats.forEach((c) => {
        if (next[c.id] === undefined) next[c.id] = true;
      });
      return next;
    });
  }, [search, catalogType]);

  const loadAudits = useCallback(async () => {
    const res = await consumableService.getAudits({ limit: 50 }, catalogType);
    setAudits(res.data || []);
  }, [catalogType]);

  const loadLowStock = useCallback(async () => {
    const res = await consumableService.getLowStock(catalogType);
    setLowStock(res.data || []);
  }, [catalogType]);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await employeeService.getAll({ limit: 500, activeOnly: 'true' });
      setEmployees(res.data || []);
    } catch (err) {
      console.error('Load employees:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      setActiveAudit(null);
      try {
        await Promise.all([loadAudits(), loadLowStock(), loadEmployees()]);
        if (!cancelled) {
          await loadCatalog();
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError(err.response?.data?.message || `Failed to load ${noun}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when catalog switches; search has its own effect
  }, [catalogType]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadCatalog().catch((err) => {
        console.error(err);
        setError(err.response?.data?.message || 'Failed to load catalog');
      });
    }, 250);
    return () => clearTimeout(t);
  }, [loadCatalog]);

  const groupedItems = useMemo(() => {
    const byCat = new Map();
    categories.forEach((c) => {
      byCat.set(c.id, { category: c, items: [] });
    });
    items.forEach((item) => {
      if (!byCat.has(item.category_id)) {
        byCat.set(item.category_id, {
          category: {
            id: item.category_id,
            name: item.category_name || 'Uncategorized',
            sort_order: item.category_sort_order || 0
          },
          items: []
        });
      }
      byCat.get(item.category_id).items.push(item);
    });
    return Array.from(byCat.values()).sort(
      (a, b) =>
        (a.category.sort_order || 0) - (b.category.sort_order || 0) ||
        String(a.category.name).localeCompare(String(b.category.name))
    );
  }, [categories, items]);

  const flatCatalogRows = useMemo(() => {
    const rows = [];
    groupedItems.forEach(({ category, items: catItems }) => {
      catItems.forEach((item) => {
        rows.push({ category, item });
      });
    });
    return rows;
  }, [groupedItems]);

  const getCatalogExportData = useCallback(() => {
    if (!flatCatalogRows.length) return null;
    const headers = isRaw
      ? [
          'Category',
          'Thickness',
          'Sheet size',
          'On hand',
          'Min',
          'Unit',
          'Notes',
          'Last updated',
          'Updated by'
        ]
      : [
          'Category',
          'Item',
          'Part #',
          'On hand',
          'Min',
          'Unit',
          'Notes',
          'Last updated',
          'Updated by'
        ];
    const rows = flatCatalogRows.map(({ category, item }) => {
      const sharedTail = [
        item.quantity_on_hand != null ? String(item.quantity_on_hand) : '',
        item.minimum_quantity != null ? String(item.minimum_quantity) : '',
        item.unit || '',
        item.notes || '',
        formatTs(item.last_counted_at) === '—' ? '' : formatTs(item.last_counted_at),
        item.last_counted_by_name?.trim() || ''
      ];
      if (isRaw) {
        return [category.name || '', item.thickness || '', item.sheet_size || '', ...sharedTail];
      }
      return [category.name || '', item.name || '', item.part_number || '', ...sharedTail];
    });
    const dateStamp = format(new Date(), 'yyyy-MM-dd');
    return {
      headers,
      rows,
      pdfTitle: `${pageTitle} inventory — ${dateStamp}`,
      filenameBase: isRaw ? `raw-materials-inventory-${dateStamp}` : `consumables-inventory-${dateStamp}`,
      pdfLandscape: true
    };
  }, [flatCatalogRows, isRaw, pageTitle]);

  const handlePrintCatalog = () => {
    if (!flatCatalogRows.length) return;
    printWithFilename(isRaw ? 'Raw-materials-inventory' : 'Consumables-inventory');
  };

  const openAuditStats = useMemo(() => {
    if (!activeAudit?.lines) return { total: 0, counted: 0, discrepancies: 0, notCounted: 0 };
    const lines = activeAudit.lines;
    const counted = lines.filter((l) => l.counted_qty != null).length;
    const discrepancies = lines.filter(
      (l) => l.counted_qty != null && Number(l.counted_qty) !== Number(l.system_qty)
    ).length;
    return {
      total: lines.length,
      counted,
      discrepancies,
      notCounted: lines.length - counted
    };
  }, [activeAudit]);

  const auditGroups = useMemo(() => {
    if (!activeAudit?.lines) return [];
    const map = new Map();
    activeAudit.lines.forEach((line) => {
      const key = line.category_id;
      if (!map.has(key)) {
        map.set(key, {
          category_id: key,
          category_name: line.category_name,
          sort_order: line.category_sort_order || 0,
          lines: []
        });
      }
      map.get(key).lines.push(line);
    });
    return Array.from(map.values()).sort(
      (a, b) => a.sort_order - b.sort_order || String(a.category_name).localeCompare(String(b.category_name))
    );
  }, [activeAudit]);

  const openNewCategory = () => {
    setCatForm({ id: null, name: '' });
    setCatDialog(true);
  };

  const openEditCategory = (category) => {
    setCatForm({ id: category.id, name: category.name || '' });
    setCatDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!catForm.name.trim()) return;
    setError('');
    try {
      if (catForm.id) {
        await consumableService.updateCategory(catForm.id, { name: catForm.name.trim() });
        setSuccess('Category updated');
      } else {
        await consumableService.createCategory({ name: catForm.name.trim() }, catalogType);
        setSuccess('Category created');
      }
      setCatDialog(false);
      setCatForm({ id: null, name: '' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (
      !window.confirm(
        `Delete category "${category.name}"? This only works if it has no active items.`
      )
    ) {
      return;
    }
    setError('');
    try {
      await consumableService.deleteCategory(category.id);
      setSuccess(`Deleted category "${category.name}"`);
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const itemLabel = (item) => {
    if (!item) return 'item';
    if (isRaw) {
      const parts = [item.thickness, item.sheet_size].filter(Boolean);
      if (parts.length) return parts.join(' — ');
    }
    return item.name || item.part_number || `#${item.id}`;
  };

  const handleDeleteItem = async (item) => {
    const label = itemLabel(item);
    const material = item.category_name ? ` (${item.category_name})` : '';
    if (
      !window.confirm(
        `Remove "${label}"${material} from the list? It will no longer appear in inventory or new audits.`
      )
    ) {
      return;
    }
    setError('');
    try {
      await consumableService.deleteItem(item.id);
      setSuccess(`Removed "${label}" from the list`);
      await Promise.all([loadCatalog(), loadLowStock()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove item');
    }
  };

  const openNewItem = (categoryId = '') => {
    setItemForm({
      id: null,
      category_id: categoryId || (categories[0]?.id ?? ''),
      name: '',
      part_number: '',
      thickness: '',
      sheet_size: '',
      quantity_on_hand: 0,
      minimum_quantity: 0,
      unit: isRaw ? 'sheet' : '',
      notes: ''
    });
    setItemDialog(true);
  };

  const openEditItem = (item) => {
    setItemForm({
      id: item.id,
      category_id: item.category_id,
      name: item.name || '',
      part_number: item.part_number || '',
      thickness: item.thickness || '',
      sheet_size: item.sheet_size || '',
      quantity_on_hand: item.quantity_on_hand ?? 0,
      minimum_quantity: item.minimum_quantity ?? 0,
      unit: item.unit || '',
      notes: item.notes || ''
    });
    setItemDialog(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.category_id) {
      setError('Category is required');
      return;
    }
    if (isRaw) {
      if (!itemForm.thickness.trim() && !itemForm.sheet_size.trim() && !itemForm.name.trim()) {
        setError('Provide thickness and sheet size (or an item name)');
        return;
      }
    } else if (!itemForm.name.trim()) {
      setError('Item name and category are required');
      return;
    }
    setError('');
    try {
      const payload = {
        category_id: parseInt(itemForm.category_id, 10),
        name: itemForm.name.trim() || null,
        part_number: itemForm.part_number.trim() || null,
        thickness: isRaw ? itemForm.thickness.trim() || null : null,
        sheet_size: isRaw ? itemForm.sheet_size.trim() || null : null,
        quantity_on_hand: parseInt(itemForm.quantity_on_hand, 10) || 0,
        minimum_quantity: parseInt(itemForm.minimum_quantity, 10) || 0,
        unit: itemForm.unit.trim() || null,
        notes: itemForm.notes || null
      };
      if (itemForm.id) {
        await consumableService.updateItem(itemForm.id, payload);
        setSuccess('Item updated');
      } else {
        await consumableService.createItem(payload);
        setSuccess('Item created');
      }
      setItemDialog(false);
      await loadCatalog();
      await loadLowStock();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save item');
    }
  };

  const openSetQty = (item) => {
    setQtyForm({
      id: item.id,
      name: item.name,
      quantity: item.quantity_on_hand ?? 0,
      notes: ''
    });
    setQtyDialog(true);
  };

  const handleSetQty = async () => {
    setError('');
    try {
      await consumableService.setQuantity(
        qtyForm.id,
        parseInt(qtyForm.quantity, 10) || 0,
        qtyForm.notes || undefined
      );
      setQtyDialog(false);
      setSuccess('On-hand quantity updated');
      await loadCatalog();
      await loadLowStock();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set quantity');
    }
  };

  const handleCreateAudit = async () => {
    setError('');
    setAuditSaving(true);
    try {
      const res = await consumableService.createAudit(
        {
          audit_date: newAuditDate,
          performed_by: newAuditPerformer?.id || user?.id || undefined
        },
        catalogType
      );
      setNewAuditOpen(false);
      setActiveAudit(res.data);
      setTab(1);
      setSuccess('Audit started — enter counts by category');
      await loadAudits();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start audit');
      if (err.response?.data?.openAuditId) {
        try {
          const open = await consumableService.getAudit(err.response.data.openAuditId);
          setActiveAudit(open.data);
          setTab(1);
        } catch (_) {
          /* ignore */
        }
      }
    } finally {
      setAuditSaving(false);
    }
  };

  const openAudit = async (id) => {
    setError('');
    try {
      const res = await consumableService.getAudit(id);
      setActiveAudit(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open audit');
    }
  };

  const handleCountChange = async (line, value) => {
    const counted = value === '' ? null : parseInt(value, 10);
    setActiveAudit((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lines: prev.lines.map((l) =>
          l.id === line.id ? { ...l, counted_qty: Number.isNaN(counted) ? l.counted_qty : counted } : l
        )
      };
    });
  };

  const persistCount = async (line) => {
    if (!activeAudit || activeAudit.status !== 'open') return;
    try {
      await consumableService.updateAuditLine(activeAudit.id, line.id, {
        counted_qty: line.counted_qty
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save count');
    }
  };

  const handlePostAudit = async () => {
    if (!activeAudit) return;
    if (!window.confirm('Post this audit? Counted quantities will become the new on-hand amounts.')) {
      return;
    }
    setAuditSaving(true);
    setError('');
    try {
      const res = await consumableService.postAudit(activeAudit.id);
      setActiveAudit(res.data);
      setSuccess('Audit posted — on-hand quantities updated');
      await Promise.all([loadCatalog(), loadAudits(), loadLowStock()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to post audit');
    } finally {
      setAuditSaving(false);
    }
  };

  const handleCancelAudit = async () => {
    if (!activeAudit) return;
    if (!window.confirm('Cancel this open audit? Counts will be discarded.')) return;
    setAuditSaving(true);
    try {
      const res = await consumableService.cancelAudit(activeAudit.id);
      setActiveAudit(res.data);
      setSuccess('Audit cancelled');
      await loadAudits();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel audit');
    } finally {
      setAuditSaving(false);
    }
  };

  const downloadCsvTemplate = () => {
    const csv = isRaw
      ? 'category,thickness,sheet_size,quantity_on_hand,minimum_quantity,unit,notes\n' +
        'Mild Steel,12 ga,120 x 48,4,0,sheet,\n' +
        'Stainless Steel,11 ga,120 x 60,1,0,sheet,\n' +
        'Aluminum,.080 (14 ga),120 x 60,5,0,sheet,\n'
      : 'category,name,part_number,quantity_on_hand,minimum_quantity,unit,notes\n' +
        'Welding,Welding Tip (Bernard T-045),T-045,6,2,ea,\n' +
        'Welding,Mig Wire (ER70S-6 .035),ER70S-6,12,4,lb,\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isRaw ? 'raw-materials-import-template.csv' : 'consumables-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImportPreview(String(reader.result || ''));
      setImportOpen(true);
    };
    reader.onerror = () => setError('Failed to read CSV file');
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImportSubmit = async () => {
    if (!importPreview.trim()) return;
    setImportSaving(true);
    setError('');
    try {
      const res = await consumableService.importCsv(importPreview, catalogType);
      const s = res.data || {};
      setImportOpen(false);
      setImportPreview('');
      setSuccess(
        `Imported ${s.created || 0} item(s)` +
          (s.categoriesCreated ? `, ${s.categoriesCreated} new categor${s.categoriesCreated === 1 ? 'y' : 'ies'}` : '') +
          (s.skipped ? `, ${s.skipped} skipped` : '')
      );
      await Promise.all([loadCatalog(), loadLowStock()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import CSV');
    } finally {
      setImportSaving(false);
    }
  };

  const toggleCat = (id) => {
    setExpandedCats((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box className="no-print">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h1">
            {pageTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pageSubtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ReportExportMenu
            disabled={loading || flatCatalogRows.length === 0}
            getData={getCatalogExportData}
            defaultFilenameBase={isRaw ? 'raw-materials-inventory' : 'consumables-inventory'}
            buttonLabel="Export"
          />
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintCatalog}
            disabled={loading || flatCatalogRows.length === 0}
          >
            Print
          </Button>
          {canManage && (
            <>
              <Button variant="outlined" onClick={openNewCategory}>
                New category
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openNewItem()}>
                New item
              </Button>
              <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                Import CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleImportFile}
              />
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewAuditPerformer(
                employees.find((e) => e.id === user?.id) || null
              );
              setNewAuditDate(format(new Date(), 'yyyy-MM-dd'));
              setNewAuditOpen(true);
            }}
          >
            New audit
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Catalog" />
        <Tab label="Audits" />
        <Tab label={`Low stock (${lowStock.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        <Box>
          <TextField
            size="small"
            placeholder={
              isRaw
                ? 'Search by material, thickness, or sheet size...'
                : 'Search by item name or part number...'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  {isRaw ? (
                    <>
                      <TableCell>Thickness</TableCell>
                      <TableCell>Sheet size</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>Item</TableCell>
                      <TableCell>Part #</TableCell>
                    </>
                  )}
                  <TableCell align="right">On hand</TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell>Last updated</TableCell>
                  <TableCell>Updated by</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No {noun} yet. {canManage ? 'Create a category, add items, or Import CSV.' : 'Ask a teammate to set up the catalog.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {groupedItems.map(({ category, items: catItems }) => {
                  const open = expandedCats[category.id] !== false;
                  const lowInCat = catItems.filter(
                    (i) => Number(i.quantity_on_hand) <= Number(i.minimum_quantity)
                  ).length;
                  return (
                    <React.Fragment key={category.id}>
                      <TableRow
                        hover
                        onClick={() => toggleCat(category.id)}
                        sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell colSpan={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight={600}>{category.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {catItems.length} item{catItems.length === 1 ? '' : 's'}
                            </Typography>
                            {lowInCat > 0 && (
                              <Chip size="small" color="warning" label={`${lowInCat} low`} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {canManage && (
                            <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                              <Button size="small" onClick={() => openEditCategory(category)}>
                                Edit
                              </Button>
                              <Button size="small" color="error" onClick={() => handleDeleteCategory(category)}>
                                Delete
                              </Button>
                              <Button size="small" onClick={() => openNewItem(category.id)}>
                                Add item
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                      {open &&
                        catItems.map((item) => {
                          const low =
                            Number(item.quantity_on_hand) <= Number(item.minimum_quantity);
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell />
                              {isRaw ? (
                                <>
                                  <TableCell>{item.thickness || '—'}</TableCell>
                                  <TableCell>{item.sheet_size || '—'}</TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>{item.part_number || '—'}</TableCell>
                                </>
                              )}
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ color: low ? 'warning.main' : 'inherit', fontWeight: low ? 600 : 400 }}
                                >
                                  {item.quantity_on_hand}
                                </Box>
                              </TableCell>
                              <TableCell align="right">{item.minimum_quantity}</TableCell>
                              <TableCell>{formatTs(item.last_counted_at)}</TableCell>
                              <TableCell>{item.last_counted_by_name?.trim() || '—'}</TableCell>
                              <TableCell align="right">
                                <Button size="small" onClick={() => openSetQty(item)}>
                                  Set qty
                                </Button>
                                {canManage && (
                                  <>
                                    <Button size="small" onClick={() => openEditItem(item)}>
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteItem(item)}
                                    >
                                      Remove
                                    </Button>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : tab === 1 ? (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 280, flex: '0 0 auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Recent audits
              </Typography>
              {audits.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No audits yet. Start Monday’s count with New audit.
                </Typography>
              )}
              {audits.map((a) => (
                <Box
                  key={a.id}
                  onClick={() => openAudit(a.id)}
                  sx={{
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: activeAudit?.id === a.id ? 'action.selected' : 'transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(a.audit_date)}
                    </Typography>
                    {statusChip(a.status)}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {a.performed_by_name?.trim() || 'Unassigned'} · {a.counted_count}/{a.line_count} counted
                    {a.discrepancy_count > 0 ? ` · ${a.discrepancy_count} variance` : ''}
                  </Typography>
                </Box>
              ))}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 320 }}>
              {!activeAudit ? (
                <Typography variant="body2" color="text.secondary">
                  Select an audit or start a new one to enter counts.
                </Typography>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      mb: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        Audit {formatDate(activeAudit.audit_date)} {statusChip(activeAudit.status)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Performed by {activeAudit.performed_by_name?.trim() || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {openAuditStats.counted}/{openAuditStats.total} counted ·{' '}
                        {openAuditStats.discrepancies} discrepancies · {openAuditStats.notCounted} not
                        counted
                      </Typography>
                    </Box>
                    {activeAudit.status === 'open' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button color="inherit" disabled={auditSaving} onClick={handleCancelAudit}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          disabled={auditSaving || openAuditStats.counted === 0}
                          onClick={handlePostAudit}
                        >
                          Post audit
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {auditGroups.map((group) => {
                    const issues = group.lines.filter(
                      (l) =>
                        l.counted_qty != null && Number(l.counted_qty) !== Number(l.system_qty)
                    ).length;
                    return (
                      <Box key={group.category_id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography fontWeight={600}>{group.category_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.lines.length} items
                          </Typography>
                          {issues > 0 && (
                            <Chip size="small" color="error" label={`${issues} issue${issues === 1 ? '' : 's'}`} />
                          )}
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell>Part #</TableCell>
                                <TableCell align="right">System</TableCell>
                                <TableCell align="right" width={120}>
                                  Counted
                                </TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.lines.map((line) => (
                                <TableRow key={line.id}>
                                  <TableCell>{line.item_name}</TableCell>
                                  <TableCell>{line.part_number || '—'}</TableCell>
                                  <TableCell align="right">{line.system_qty}</TableCell>
                                  <TableCell align="right">
                                    {activeAudit.status === 'open' ? (
                                      <TextField
                                        size="small"
                                        type="number"
                                        value={line.counted_qty ?? ''}
                                        onChange={(e) => handleCountChange(line, e.target.value)}
                                        onBlur={(e) => {
                                          const raw = e.target.value;
                                          const counted = raw === '' ? null : parseInt(raw, 10);
                                          persistCount({
                                            ...line,
                                            counted_qty: Number.isNaN(counted) ? null : counted
                                          });
                                        }}
                                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                        sx={{ width: 100 }}
                                      />
                                    ) : (
                                      line.counted_qty ?? '—'
                                    )}
                                  </TableCell>
                                  <TableCell>{statusChip(lineStatus(line))}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Part #</TableCell>
                <TableCell align="right">On hand</TableCell>
                <TableCell align="right">Min</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lowStock.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No low-stock {noun}.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {lowStock.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.category_name}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.part_number || '—'}</TableCell>
                  <TableCell align="right">{item.quantity_on_hand}</TableCell>
                  <TableCell align="right">{item.minimum_quantity}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openSetQty(item)}>
                      Set qty
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Category dialog */}
      <Dialog open={catDialog} onClose={() => setCatDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{catForm.id ? 'Edit category' : 'New category'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Category name"
            value={catForm.name}
            onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mt: 1 }}
            placeholder="e.g. Welding"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory} disabled={!catForm.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item dialog */}
      <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{itemForm.id ? 'Edit item' : 'New item'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{isRaw ? 'Material' : 'Category'}</InputLabel>
              <Select
                label={isRaw ? 'Material' : 'Category'}
                value={itemForm.category_id}
                onChange={(e) => setItemForm((f) => ({ ...f, category_id: e.target.value }))}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isRaw ? (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Thickness"
                    fullWidth
                    size="small"
                    value={itemForm.thickness}
                    onChange={(e) => setItemForm((f) => ({ ...f, thickness: e.target.value }))}
                    placeholder="e.g. 12 ga or 1/4 in"
                  />
                  <TextField
                    label="Sheet size"
                    fullWidth
                    size="small"
                    value={itemForm.sheet_size}
                    onChange={(e) => setItemForm((f) => ({ ...f, sheet_size: e.target.value }))}
                    placeholder="e.g. 120 x 60"
                  />
                </Box>
                <TextField
                  label="Display name (optional)"
                  fullWidth
                  size="small"
                  value={itemForm.name}
                  onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Defaults to thickness — sheet size"
                />
              </>
            ) : (
              <>
                <TextField
                  label="Item name"
                  fullWidth
                  size="small"
                  value={itemForm.name}
                  onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Welding Tip (Bernard T-045)"
                />
                <TextField
                  label="Part #"
                  fullWidth
                  size="small"
                  value={itemForm.part_number}
                  onChange={(e) => setItemForm((f) => ({ ...f, part_number: e.target.value }))}
                />
              </>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="On hand"
                type="number"
                size="small"
                fullWidth
                value={itemForm.quantity_on_hand}
                onChange={(e) => setItemForm((f) => ({ ...f, quantity_on_hand: e.target.value }))}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Minimum"
                type="number"
                size="small"
                fullWidth
                value={itemForm.minimum_quantity}
                onChange={(e) => setItemForm((f) => ({ ...f, minimum_quantity: e.target.value }))}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Unit"
                size="small"
                fullWidth
                value={itemForm.unit}
                onChange={(e) => setItemForm((f) => ({ ...f, unit: e.target.value }))}
                placeholder={isRaw ? 'sheet' : 'ea'}
              />
            </Box>
            <TextField
              label="Notes"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={itemForm.notes}
              onChange={(e) => setItemForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveItem}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set quantity */}
      <Dialog open={qtyDialog} onClose={() => setQtyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set on hand — {qtyForm.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Quantity on hand"
            value={qtyForm.quantity}
            onChange={(e) => setQtyForm((f) => ({ ...f, quantity: e.target.value }))}
            sx={{ mt: 1 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            value={qtyForm.notes}
            onChange={(e) => setQtyForm((f) => ({ ...f, notes: e.target.value }))}
            sx={{ mt: 2 }}
            placeholder="Initial inventory load"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQtyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSetQty}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV */}
      <Dialog open={importOpen} onClose={() => !importSaving && setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import {pageTitle.toLowerCase()} CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {isRaw
              ? 'Columns: category (material), thickness, sheet_size, quantity_on_hand, minimum_quantity, unit, notes. Missing materials are created automatically.'
              : 'Columns: category, name, part_number, quantity_on_hand, minimum_quantity, unit, notes. Missing categories are created automatically.'}{' '}
            Each imported row is attributed to you.
          </Typography>
          <Button size="small" onClick={downloadCsvTemplate} sx={{ mb: 1 }}>
            Download template
          </Button>
          <TextField
            fullWidth
            multiline
            minRows={12}
            value={importPreview}
            onChange={(e) => setImportPreview(e.target.value)}
            placeholder="Paste CSV or choose a file…"
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportSubmit}
            disabled={importSaving || !importPreview.trim()}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* New audit */}
      <Dialog open={newAuditOpen} onClose={() => setNewAuditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New weekly audit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="Audit date"
            value={newAuditDate}
            onChange={(e) => setNewAuditDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, mb: 2 }}
          />
          <Autocomplete
            options={employees}
            getOptionLabel={employeeLabel}
            value={newAuditPerformer}
            onChange={(_, val) => setNewAuditPerformer(val)}
            renderInput={(params) => <TextField {...params} label="Performed by" size="small" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewAuditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAudit} disabled={auditSaving || !newAuditDate}>
            Start audit
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      <PrintPage title={`${pageTitle} inventory`} showSpacer={false}>
        <div className="print-section" style={{ marginBottom: 12 }}>
          <span style={{ color: '#444' }}>
            As of {format(new Date(), 'MM/dd/yyyy h:mm a')}
            {search.trim() ? ` · Filtered: “${search.trim()}”` : ''}
            {' · '}
            {flatCatalogRows.length} item{flatCatalogRows.length === 1 ? '' : 's'}
          </span>
        </div>
        {flatCatalogRows.length === 0 ? (
          <p style={{ fontSize: 13 }}>No {noun} to print.</p>
        ) : (
          <table className="print-table">
            <thead>
              <tr>
                <th>{isRaw ? 'Material' : 'Category'}</th>
                {isRaw ? (
                  <>
                    <th>Thickness</th>
                    <th>Sheet size</th>
                  </>
                ) : (
                  <>
                    <th>Item</th>
                    <th>Part #</th>
                  </>
                )}
                <th style={{ textAlign: 'right' }}>On hand</th>
                <th style={{ textAlign: 'right' }}>Min</th>
                <th>Last updated</th>
                <th>Updated by</th>
              </tr>
            </thead>
            <tbody>
              {flatCatalogRows.map(({ category, item }) => (
                <tr key={item.id}>
                  <td>{category.name || '—'}</td>
                  {isRaw ? (
                    <>
                      <td>{item.thickness || '—'}</td>
                      <td>{item.sheet_size || '—'}</td>
                    </>
                  ) : (
                    <>
                      <td>{item.name || '—'}</td>
                      <td>{item.part_number || '—'}</td>
                    </>
                  )}
                  <td style={{ textAlign: 'right' }}>{item.quantity_on_hand}</td>
                  <td style={{ textAlign: 'right' }}>{item.minimum_quantity}</td>
                  <td>{formatTs(item.last_counted_at)}</td>
                  <td>{item.last_counted_by_name?.trim() || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PrintPage>
    </Box>
  );
};

export default Consumables;
